import { useEffect, useState } from 'react';
import { Util } from 'pdfjs-dist';
import { openUserContentUrl } from '../lib/external';
import { isDesktop } from '../lib/platform';

// Only ever render a clickable anchor for these URI schemes. Anything else
// (javascript:, file:, ftp:, data:, etc.) renders NOTHING — not even a dead
// anchor — so a malicious/unexpected annotation can never execute or probe
// the local filesystem via a click.
const ALLOWED_URL_SCHEME = /^(https?:|mailto:)/i;

/**
 * Resolve a Link annotation's target into a small render-ready descriptor,
 * or null if it can't/shouldn't be rendered (disallowed scheme, unresolvable
 * dest, or neither url nor dest present).
 */
async function resolveLinkTarget(annotation, pdfDoc) {
  if (annotation.url) {
    if (!ALLOWED_URL_SCHEME.test(annotation.url)) return null;
    return { kind: 'external', url: annotation.url };
  }
  if (annotation.dest) {
    try {
      const explicitDest =
        typeof annotation.dest === 'string'
          ? await pdfDoc.getDestination(annotation.dest)
          : annotation.dest;
      if (!explicitDest || !explicitDest[0]) return null;
      const pageIndex = await pdfDoc.getPageIndex(explicitDest[0]);
      return { kind: 'internal', pageIndex };
    } catch {
      // Malformed/unresolvable dest — render nothing rather than a dead link.
      return null;
    }
  }
  return null;
}

/**
 * PdfLinkLayer — per-page overlay that turns PDF `/Link` annotations into
 * clickable anchors positioned over their annotation rect.
 *
 * Mounted inside the SAME renderOverlay slot as PdfTextLayer + SearchHighlightLayer
 * (text layer, then highlights, then this), so it shares DOM-order stacking
 * with those overlays — BEFORE MaskLayer/AnnotationLayer/EditLayer are mounted
 * as later, separate slots. Per the Task 2 stacking rule (see PdfTextLayer.jsx
 * and its regression test): EditLayer/AnnotationLayer/MaskLayer roots are
 * z-auto, so any explicit positive z-index on a sibling here would paint
 * ABOVE them and steal their clicks. This root sets NO z-index and is itself
 * pointerEvents:'none' — only the individual anchor children opt into
 * pointerEvents:'auto', so link hit-targets exist only where links actually
 * are, and DOM order (this layer first) means an EditItem/annotation drawn
 * over a link rect still wins the click, matching Task 2's fixed semantics.
 */
export default function PdfLinkLayer({ pdfDoc, pageNum, scale, interactive = true, onGoToPage }) {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    if (!pdfDoc) {
      setLinks([]);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (cancelled) return;
        const annotations = await page.getAnnotations();
        if (cancelled) return;
        const viewport = page.getViewport({ scale });

        const linkAnnotations = annotations.filter((a) => a.subtype === 'Link' && a.rect);
        const resolved = await Promise.all(
          linkAnnotations.map(async (annotation) => {
            const target = await resolveLinkTarget(annotation, pdfDoc);
            if (!target) return null;
            const viewportRect = Util.normalizeRect(viewport.convertToViewportRectangle(annotation.rect));
            const [left, top, right, bottom] = viewportRect;
            return {
              id: annotation.id ?? `${left},${top},${right},${bottom}`,
              left,
              top,
              width: right - left,
              height: bottom - top,
              target,
            };
          })
        );
        if (cancelled) return;
        setLinks(resolved.filter(Boolean));
      } catch {
        // Never break the viewer over a link-layer failure — the page simply
        // has no clickable links.
        if (!cancelled) setLinks([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNum, scale]);

  const handleExternalClick = (url) => (e) => {
    e.preventDefault();
    if (isDesktop()) {
      const opened = openUserContentUrl(url);
      if (!opened && import.meta.env?.DEV) console.warn('external link rejected', url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleInternalClick = (pageIndex) => (e) => {
    e.preventDefault();
    onGoToPage?.(pageIndex + 1);
  };

  return (
    <div
      className="absolute inset-0"
      style={{ pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {links.map((link) => {
        const commonStyle = {
          position: 'absolute',
          left: `${link.left}px`,
          top: `${link.top}px`,
          width: `${link.width}px`,
          height: `${link.height}px`,
          display: 'block',
          pointerEvents: interactive ? 'auto' : 'none',
        };

        if (link.target.kind === 'external') {
          return (
            <a
              key={link.id}
              href={link.target.url}
              title={link.target.url}
              style={commonStyle}
              onClick={handleExternalClick(link.target.url)}
            />
          );
        }

        return (
          <button
            key={link.id}
            type="button"
            style={{ ...commonStyle, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
            onClick={handleInternalClick(link.target.pageIndex)}
          />
        );
      })}
    </div>
  );
}
